import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import {
  isInvolvementKindHeldByOneAtMost,
  PersonInvolvementKindSchema,
  type ActivityEntry,
  type PersonId,
  type PersonInvolvement,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import {
  ACTIVITY_LOG_GROWTH,
  useGrowingLimit,
} from "../../hooks/useGrowingLimit.js";
import {
  ActivityLog,
  type DescribedEntry,
  type Translate,
} from "../ActivityLog/ActivityLog.js";
import { describeAssessmentAct } from "../AssessmentActivity/AssessmentActivity.js";

/**
 * Whether an ending passed an involvement to its next holder: a kind held one
 * at a time, ended as superseded, rather than reach being taken away.
 */
const isHandover = (entry: ActivityEntry): boolean => {
  const kind = PersonInvolvementKindSchema.safeParse(entry.data.kind);

  return (
    entry.kind === "involvement_ended" &&
    entry.data.cause === "superseded" &&
    kind.success &&
    isInvolvementKindHeldByOneAtMost(kind.data)
  );
};

const ENDING_DAY = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "medium",
  timeZone: "Europe/London",
});

/**
 * The day an ending set for one takes effect, and whether that has now passed.
 * `null` for one that took effect at once, or whose payload predates saying
 * when.
 *
 * An ending at once takes the database's `NOW()` (see
 * `PersonInvolvementRepository.endAll`), never later than the entry recording
 * it, so any ending later than its entry was set for a day.
 */
const endingSetForADay = (
  entry: ActivityEntry,
  now: Date,
): { date: string; passed: boolean } | null => {
  const endedAt = entry.data.endedAt;
  if (typeof endedAt !== "string") {
    return null;
  }

  const instant = new Date(endedAt);

  // Negated rather than `<=`, so an instant that cannot be read is no ending.
  if (!(instant > new Date(entry.timestamp))) {
    return null;
  }

  return { date: ENDING_DAY.format(instant), passed: instant <= now };
};

/**
 * What an involvement entry was about, as far as the page can say.
 *
 * The involvement payloads record the kind and not the party, so the party is
 * read off the involvement this page already holds.
 *
 * A miss is ordinary rather than exceptional: an involvement the viewer cannot
 * see, or one on a record outside the set fetched. The kind of involvement is
 * still worth saying, so the fallback says it alone. `null` is the third case —
 * a payload this build cannot read at all, where there is nothing to say.
 */
const involvementSubject = (
  entry: ActivityEntry,
  involvements: readonly PersonInvolvement[],
  t: Translate,
): string | null => {
  const kind = entry.data.kind;

  if (typeof kind !== "string") {
    return null;
  }

  const named = entry.resources.find(
    (resource) => resource.resourceKind === "person_involvement",
  );

  const involvement = involvements.find(
    (involvement) => involvement.id === named?.resourceId,
  );

  const label = t(`access.kind.${kind}`, { defaultValue: kind });

  return involvement === undefined
    ? t("personPage.activity.involvement", { kind: label })
    : t("personPage.activity.involvementWithParty", {
        kind: label,
        party: involvement.party.name,
      });
};

/**
 * Which parts of the record a correction changed.
 *
 * Named because "Record corrected" on its own is the same sentence however many
 * times the record is touched. The trail records which parts changed and never
 * their values, so this says what was revisited rather than what it became.
 */
const correctionSubject = (entry: ActivityEntry, t: Translate): string => {
  const changed = entry.data.fields;

  const named = Array.isArray(changed)
    ? changed.filter((field): field is string => typeof field === "string")
    : [];

  return named.length === 0
    ? t("personPage.activity.event.person_updated")
    : t("personPage.activity.corrected", {
        fields: named
          .map((field) =>
            t(`personPage.activity.field.${field}`, { defaultValue: field }),
          )
          .join(", "),
      });
};

/**
 * "Ended", or for an ending set for a day, the day it ends or ended. The entry
 * is written when the ending is set, so whether it has happened yet is read
 * against the present.
 */
const endingLabel = (
  dayEnding: { date: string; passed: boolean } | null,
  t: Translate,
): string => {
  if (dayEnding === null) {
    return t("personPage.activity.ended");
  }

  return dayEnding.passed
    ? t("personPage.activity.endedOn", { date: dayEnding.date })
    : t("personPage.activity.endsOn", { date: dayEnding.date });
};

/** An ending said with why it ended, where the entry says why. */
const withCause = (change: string, why: string | null, t: Translate): string =>
  why === null
    ? change
    : t("personPage.activity.changeWithCause", { change, cause: why });

/**
 * What one entry says about the record.
 *
 * The subject leads: an entry's act is "involvement added" a dozen times over,
 * and what a reader is looking for is which involvement and whose. So the thing
 * acted upon is the line, and the act is a mark beside it.
 */
const describeEntry =
  (involvements: readonly PersonInvolvement[]) =>
  (entry: ActivityEntry, t: Translate): DescribedEntry => {
    const act = t(`personPage.activity.event.${entry.kind}`, {
      defaultValue: entry.kind,
    });

    const involvement =
      entry.kind === "involvement_started" || entry.kind === "involvement_ended"
        ? involvementSubject(entry, involvements, t)
        : null;

    const cause = entry.data.cause;
    const dayEnding = endingSetForADay(entry, new Date());
    // An ending still to come has handed nothing over yet, so until it takes
    // effect it says when it will.
    const handedOver =
      isHandover(entry) && (dayEnding === null || dayEnding.passed);

    const why =
      typeof cause === "string"
        ? t(`access.cause.${cause}`, { defaultValue: cause })
        : null;

    return {
      subject:
        involvement ??
        (entry.kind === "person_updated"
          ? correctionSubject(entry, t)
          : (describeAssessmentAct(entry, t) ?? act)),

      // Only where the subject names the involvement. Where it fell back to the
      // act, a mark saying "added" beside a line saying "Involvement added"
      // would be the same word twice.
      change:
        involvement === null
          ? null
          : handedOver
            ? {
                label: t("personPage.activity.handedOver"),
                description: t("personPage.activity.involvementHandedOver"),
                ended: true,
              }
            : entry.kind === "involvement_ended"
              ? {
                  label: withCause(endingLabel(dayEnding, t), why, t),
                  description: withCause(act, why, t),
                  ended: true,
                }
              : {
                  label: t("personPage.activity.added"),
                  description: act,
                  ended: false,
                },

      // A handover's words are the only why it has; any other ending's cause
      // already says why, so the words add to it.
      reasonAs:
        entry.kind === "involvement_ended" && !isHandover(entry)
          ? "additional-information"
          : "reason",
    };
  };

export type ActivityLogDialogContentProps = {
  entries: ActivityEntry[];
  count: number;
  involvements: readonly PersonInvolvement[];
  loading: boolean;
  errored: boolean;
  /**
   * Whether the dialog takes the whole screen.
   *
   * A question about the window rather than about the entries, which lay
   * themselves out the same way at every width. Injected rather than measured
   * here so either can be put in front of somebody — in a story, or in a test —
   * without driving a viewport.
   */
  narrow: boolean;
  /**
   * Asks for more of the log. Absent where no further page can be asked for,
   * which is also what makes the count line say which part of the log is held.
   */
  onShowMore?: () => Promisable<void>;
  onClose: () => void;
};

/**
 * The log itself, given everything it renders.
 *
 * Split from the container so a story and a test can put a log in front of
 * somebody without a server — the same split `PersonPageContent` uses.
 */
export const ActivityLogDialogContent = ({
  entries,
  count,
  involvements,
  loading,
  errored,
  narrow,
  onShowMore,
  onClose,
}: ActivityLogDialogContentProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open fullWidth maxWidth="md" fullScreen={narrow} onClose={onClose}>
      <DialogTitle>{t("personPage.activity.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t("personPage.activity.description")}
          </Typography>

          <ActivityLog
            entries={entries}
            count={count}
            loading={loading}
            errored={errored}
            empty={t("personPage.activity.empty")}
            describe={describeEntry(involvements)}
            onShowMore={onShowMore}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t("personPage.activity.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export type ActivityLogDialogProps = {
  personId: PersonId;
  involvements: readonly PersonInvolvement[];
  onClose: () => void;
};

export const ActivityLogDialog = ({
  personId,
  involvements,
  onClose,
}: ActivityLogDialogProps) => {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("sm"));

  const { limit, onShowMore } = useGrowingLimit(ACTIVITY_LOG_GROWTH);

  const [{ data, loading, error }] = apiHooks.personActivity.forPerson(
    personId,
    { limit, offset: 0 },
  );

  return (
    <ActivityLogDialogContent
      entries={data?.entries ?? []}
      count={data?.count ?? 0}
      involvements={involvements}
      loading={loading}
      errored={error !== undefined && error !== null}
      narrow={narrow}
      onShowMore={onShowMore}
      onClose={onClose}
    />
  );
};
